// 261010 UI-assettabs — luật THUẦN (không antd) cho thẻ + đầu tab của bước 2 «Tài sản»: nhãn bộ đồ đọc được, trạng thái hàng chờ hướng bối cảnh → biểu tượng, chữ đầu tab.
// Component import antd không nạp dưới jest ⇒ mọi quyết định chữ/biểu tượng nằm ở đây và có test.
import type { StateTone } from './workflowBoardUtils';
import type { IconName } from './workflowStatusIcons';

const fold = (s: string): string => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D').toLowerCase();

/**
 * Nhãn bộ đồ đọc được từ khoá `lookKey`: bỏ tiền tố là tên nhân vật («Marcus-arena» của «Marcus Aurelius» ⇒ «arena»),
 * `-`/`_` thành khoảng trắng; `default` ⇒ «mặc định». Chưa có bảng dịch (arena → đấu trường…) trong code nên KHÔNG tự dịch.
 * Bỏ hết thì giữ nguyên khoá (không bao giờ trả chuỗi rỗng).
 */
export function lookLabelOf(lookKey: string | null | undefined, characterName?: string | null): string {
  const raw = String(lookKey ?? '').trim();
  if (!raw) return 'mặc định';
  if (fold(raw) === 'default') return 'mặc định';
  const nameTokens = new Set(fold(characterName ?? '').split(/[^a-z0-9]+/).filter(Boolean));
  const parts = raw.split(/[-_\s]+/).filter(Boolean);
  let i = 0;
  while (i < parts.length && nameTokens.has(fold(parts[i]))) i += 1;
  // Cả khoá chỉ gồm tên nhân vật (hoặc không có tiền tố) ⇒ giữ nguyên khoá, không có gì để rút gọn.
  if (i === 0 || i === parts.length) return parts.join(' ') || raw;
  return parts.slice(i).join(' ');
}

export interface QueueView { icon: IconName; tone: StateTone; label: string }

/**
 * Trạng thái hàng chờ ảnh hướng của MỘT bối cảnh (`WorkflowLocationDirections.phase`) → biểu tượng + Tooltip. `null` = không vẽ gì (nhàn rỗi / vừa xong).
 * Không bao giờ lộ mã thô (IDLE / RUNNING …).
 */
export function directionQueueViewOf(phase: string | null | undefined): QueueView | null {
  switch (phase) {
    case undefined: case null: case '': case 'IDLE': case 'DONE': return null;
    case 'RUNNING': return { icon: 'loading', tone: 'doing', label: 'Đang tạo ảnh hướng' };
    case 'WAITING_BANK': return { icon: 'clock', tone: 'warn', label: 'Đang đợi Bank' };
    case 'UNKNOWN': return { icon: 'warning', tone: 'bad', label: 'Kết quả chưa rõ — dừng đối soát' };
    case 'ERROR': return { icon: 'close', tone: 'bad', label: 'Có hướng bị lỗi' };
    default: return { icon: 'info', tone: 'todo', label: 'Đang xử lý' };
  }
}

/** Trạng thái một ảnh hướng (`LocationView.status`) → biểu tượng + chữ Tooltip. Mã lạ ⇒ icon info, KHÔNG hiện mã. */
export function viewStatusViewOf(status: string | null | undefined): QueueView {
  switch (status) {
    case 'EMPTY': return { icon: 'clock', tone: 'todo', label: 'Chưa có ảnh' };
    case 'ERROR': return { icon: 'close', tone: 'bad', label: 'Tạo ảnh lỗi' };
    case 'STALE': return { icon: 'sync', tone: 'warn', label: 'Ảnh cũ — ảnh chuẩn đã đổi' };
    case 'GENERATING': return { icon: 'loading', tone: 'doing', label: 'Đang tạo ảnh' };
    case 'FRESH': return { icon: 'check', tone: 'ready', label: 'Có ảnh, chưa duyệt' };
    case 'APPROVED': return { icon: 'check', tone: 'ok', label: 'Đã duyệt' };
    case 'UNKNOWN': return { icon: 'warning', tone: 'bad', label: 'Chưa rõ kết quả' };
    default: return { icon: 'info', tone: 'todo', label: 'Trạng thái chưa rõ' };
  }
}

/** Đầu mỗi tab: tiêu đề + dòng phụ + nhãn công tắc lọc. Giữ chữ cũ của tab, chỉ làm rõ nhãn công tắc. */
export const ASSET_TAB_HEAD = {
  characters: { title: 'Nhân vật', sub: 'dùng chung cho mọi tập của truyện' },
  locations: { title: 'Bối cảnh', sub: 'dùng chung cho mọi tập của truyện' },
  props: { title: 'Đạo cụ', sub: 'chỉ món khán giả thấy nhiều lần hoặc thấy cận' },
} as const;

/** Nhãn công tắc lọc: bật ⇒ «Chỉ hiện của tập này · 5/5» (đang thấy/tổng); tắt hoặc khoá ⇒ «Chỉ hiện của tập này» (lý do/cả bộ ở Tooltip). */
export function episodeToggleLabelOf(opts: { on: boolean; filterable: boolean; shown: number; total: number }): string {
  return opts.filterable && opts.on ? `Chỉ hiện của tập này · ${opts.shown}/${opts.total}` : 'Chỉ hiện của tập này';
}

/** Nhãn tiếng Việt của khối mô tả bối cảnh / bố cục / khu vực (thay Visual Description…). */
export const LOCATION_LABELS = {
  desc: 'Mô tả hình ảnh',
  aiWrite: 'AI viết mô tả',
  aiRewrite: 'AI viết lại mô tả',
  emptyDesc: 'Chưa có mô tả hình ảnh — bấm để viết, hoặc để AI viết',
  layout: 'Bố cục không gian',
  zones: 'Khu vực',
  source: 'Nguồn từ kịch bản',
} as const;
